/**
 * Baseball Clipping Service
 * Frontend API service for AI-powered play detection and clip extraction
 * Supports at-bats, pitch sequences, defensive plays, and more
 */

import { getAuthToken } from '../utils/helpers';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4001';

/**
 * Analyze a baseball video and extract individual plays
 *
 * @param {File} videoFile - The video file to analyze
 * @param {Object} options - Analysis options
 * @param {Function} options.onProgress - Progress callback (0-100)
 * @param {AbortSignal} options.signal - AbortController signal for cancellation
 * @returns {Promise<Object>} Analysis results with clips
 */
export async function analyzeGameVideo(videoFile, options = {}) {
  const { onProgress, signal, focusPrompt } = options;

  // Create form data for upload
  const formData = new FormData();
  formData.append('video', videoFile);
  formData.append('sport', 'baseball');

  // Add optional focus prompt
  if (focusPrompt) {
    formData.append('focusPrompt', focusPrompt);
  }

  // Report initial progress
  if (onProgress) {
    onProgress(5, 'Preparing upload...');
  }

  try {
    // Make the API request - use generic analyze-clips endpoint with sport param
    const response = await fetch(`${API_BASE_URL}/api/video/analyze-clips`, {
      method: 'POST',
      headers: {
        Authorization: getAuthToken(),
        'X-Sport-Code': 'CBB', // College Baseball
      },
      body: formData,
      signal,
    });

    // Report upload complete
    if (onProgress) {
      onProgress(30, 'Uploading to AI service...');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Upload failed: ${response.status}`);
    }

    // Report analysis in progress
    if (onProgress) {
      onProgress(60, 'Analyzing plays...');
    }

    const result = await response.json();

    // Report complete
    if (onProgress) {
      onProgress(100, 'Analysis complete!');
    }

    return result;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Upload cancelled');
    }
    throw error;
  }
}

/**
 * Get available play types for baseball
 *
 * @returns {Promise<Object>} Play types and color mapping
 */
export async function getPlayTypes() {
  // Return static play types for baseball
  return {
    playTypes: [
      'At-Bat',
      'Pitch Sequence',
      'Strikeout',
      'Walk',
      'Hit',
      'Home Run',
      'Double Play',
      'Defensive Play',
      'Stolen Base',
      'Pickoff',
      'Wild Pitch',
      'Bunt',
      'Sacrifice Fly',
      'Error',
      'Infield Hit',
      'Outfield Fly',
    ],
    colors: PLAY_TYPE_COLORS,
  };
}

/**
 * Baseball play type badge colors
 */
export const PLAY_TYPE_COLORS = {
  'At-Bat': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Pitch Sequence': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'Strikeout': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Walk': 'bg-green-500/20 text-green-400 border-green-500/30',
  'Hit': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Home Run': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  'Double Play': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'Defensive Play': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'Stolen Base': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'Pickoff': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  'Wild Pitch': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Bunt': 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  'Sacrifice Fly': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  'Error': 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  'Infield Hit': 'bg-lime-500/20 text-lime-400 border-lime-500/30',
  'Outfield Fly': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

/**
 * Format duration from seconds to MM:SS string
 *
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted time string
 */
export function formatDuration(seconds) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format timestamp from seconds to MM:SS string
 *
 * @param {number} seconds - Timestamp in seconds
 * @returns {string} Formatted timestamp
 */
export function formatTimestamp(seconds) {
  return formatDuration(seconds);
}

/**
 * Get CSS classes for a play type badge
 *
 * @param {string} playType - The play type
 * @param {Object} colorMap - Color mapping from API
 * @returns {string} CSS class string
 */
export function getPlayTypeClasses(playType, colorMap = PLAY_TYPE_COLORS) {
  const colors = colorMap[playType] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  return typeof colors === 'string' ? colors : `${colors.bg} ${colors.text} border ${colors.border}`;
}

/**
 * Group clips by play type for summary display
 *
 * @param {Array} clips - Array of clip objects
 * @returns {Object} Clips grouped by playType
 */
export function groupClipsByType(clips) {
  return clips.reduce((acc, clip) => {
    const type = clip.playType || 'Unknown';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(clip);
    return acc;
  }, {});
}

/**
 * Sort clips by various criteria
 *
 * @param {Array} clips - Array of clip objects
 * @param {string} sortBy - Sort criterion: 'time', 'type', 'confidence', 'inning'
 * @param {string} order - 'asc' or 'desc'
 * @returns {Array} Sorted clips
 */
export function sortClips(clips, sortBy = 'time', order = 'asc') {
  const sorted = [...clips].sort((a, b) => {
    switch (sortBy) {
      case 'time':
        return a.startTime - b.startTime;
      case 'type':
        return a.playType.localeCompare(b.playType);
      case 'confidence':
        return b.confidence - a.confidence;
      case 'inning':
        return (a.inning || 0) - (b.inning || 0);
      default:
        return 0;
    }
  });

  return order === 'desc' ? sorted.reverse() : sorted;
}

/**
 * Filter clips by play type
 *
 * @param {Array} clips - Array of clip objects
 * @param {Array} playTypes - Array of play types to include (empty = all)
 * @returns {Array} Filtered clips
 */
export function filterClipsByType(clips, playTypes = []) {
  if (!playTypes || playTypes.length === 0) {
    return clips;
  }
  return clips.filter(clip => playTypes.includes(clip.playType));
}

export default {
  analyzeGameVideo,
  getPlayTypes,
  formatDuration,
  formatTimestamp,
  getPlayTypeClasses,
  groupClipsByType,
  sortClips,
  filterClipsByType,
  PLAY_TYPE_COLORS,
};
